import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    ChevronDown, 
    ChevronUp, 
    Layers, 
    Layout, 
    Database, 
    Globe, 
    ListOrdered, 
    CheckSquare,
    Copy,
    Download,
    Check,
    Wand2,
    Zap,
    X,
    ExternalLink,
    Share2,
    Loader2,
    Rocket,
    Link,
    FileText,
    MessageSquare,
    Code,
    Terminal,
    Sparkles,
    CheckCircle,
    Users
} from 'lucide-react';
import { CollaborationModal } from './CollaborationModal';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { blueprintToMarkdown, downloadMarkdown, copyToClipboard } from '../utils/markdown';
import { generateBuilderPrompt, generateQuickPrompt, getBuilderConfigs } from '../utils/promptGenerator';
import { launchEmrg } from '../utils/emrgIntegration';
import { toast } from 'sonner';
import { 
    trackBuilderPromptCopied, 
    trackBuilderSelected,
    trackBuildOptionSelected,
    trackBlueprintCopied, 
    trackBlueprintDownloaded,
    trackEmrgButtonClicked,
    isFromSharedView,
    clearSharedViewFlag
} from '../utils/analytics';
import { BlueprintRating } from './BlueprintRating';
import { ContinueBuilding } from './ContinueBuilding';

const SectionHeader = ({ icon: Icon, title, count, isOpen, onClick, color }) => (
    <button
        onClick={onClick}
        className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors rounded-lg"
    >
        <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${color}`}>
                <Icon className="w-5 h-5" />
            </div>
            <span className="font-heading font-medium text-lg">{title}</span>
            {count !== undefined && (
                <Badge variant="secondary" className="bg-white/10 text-gray-400 text-xs">
                    {count}
                </Badge>
            )}
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
    </button>
);

const FeatureCard = ({ feature, index }) => {
    const priorityStyles = {
        core: 'badge-core',
        'nice-to-have': 'badge-nice',
        future: 'badge-future'
    };

    return (
        <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
            className="p-4 bg-surface rounded-lg border border-white/5 hover:border-primary/20 transition-all"
        >
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                    <h4 className="font-medium text-white mb-1">{feature.name}</h4>
                    <p className="text-sm text-gray-400">{feature.description}</p>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${priorityStyles[feature.priority] || 'badge-core'}`}>
                    {feature.priority}
                </span>
            </div>
        </motion.div>
    );
};

const ScreenCard = ({ screen, index }) => (
    <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.05 }}
        className="p-4 bg-surface rounded-lg border border-white/5"
    >
        <h4 className="font-medium text-white mb-2">{screen.name}</h4>
        <p className="text-sm text-gray-400 mb-3">{screen.purpose}</p>
        <div className="flex flex-wrap gap-2">
            {screen.components.map((comp, i) => (
                <span key={i} className="px-2 py-1 text-xs bg-white/5 rounded text-gray-300">
                    {comp}
                </span>
            ))}
        </div>
    </motion.div>
);

const DatabaseCard = ({ table, index }) => (
    <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.05 }}
        className="p-4 bg-surface rounded-lg border border-white/5"
    >
        <h4 className="font-mono text-primary mb-3">{table.name}</h4>
        <div className="space-y-1">
            {table.fields.map((field, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                    <span className="w-2 h-2 rounded-full bg-accent/50" />
                    <span className="font-mono text-gray-300">{field}</span>
                </div>
            ))}
        </div>
        {table.relationships && (
            <p className="mt-3 text-xs text-gray-500 italic">{table.relationships}</p>
        )}
    </motion.div>
);

const APIEndpointRow = ({ endpoint, index }) => {
    const methodColors = {
        GET: 'method-get',
        POST: 'method-post',
        PUT: 'method-put',
        DELETE: 'method-delete'
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: index * 0.03 }}
            className="flex items-center gap-4 p-3 bg-surface rounded-lg border border-white/5 text-sm"
        >
            <span className={`font-mono font-semibold w-16 ${methodColors[endpoint.method] || 'text-gray-400'}`}>
                {endpoint.method}
            </span>
            <code className="font-mono text-gray-300 flex-1">{endpoint.path}</code>
            <span className="text-gray-500 text-xs hidden md:block">{endpoint.description}</span>
        </motion.div>
    );
};

const BuildPhase = ({ step, index }) => (
    <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: index * 0.1 }}
        className="relative pl-8 pb-6"
    >
        <div className="absolute left-0 top-0 w-6 h-6 rounded-full bg-primary flex items-center justify-center text-xs font-bold">
            {step.order}
        </div>
        {index < 5 && <div className="absolute left-[11px] top-6 w-0.5 h-full bg-primary/20" />}
        <div className="ml-4">
            <h4 className="font-medium text-white mb-2">{step.phase}</h4>
            <ul className="space-y-1">
                {step.tasks.map((task, i) => (
                    <li key={i} className="text-sm text-gray-400 flex items-start gap-2">
                        <span className="text-primary mt-1">•</span>
                        {task}
                    </li>
                ))}
            </ul>
        </div>
    </motion.div>
);

const ChecklistSection = ({ checklist, onToggle }) => {
    const categories = [...new Set(checklist.map(item => item.category))];
    const completed = checklist.filter(item => item.completed).length;
    const progress = (completed / checklist.length) * 100;

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-4 mb-4">
                <Progress value={progress} className="flex-1 h-2" />
                <span className="text-sm text-gray-400">{completed}/{checklist.length}</span>
            </div>
            {categories.map(category => (
                <div key={category} className="space-y-2">
                    <h5 className="text-sm font-medium text-gray-300 uppercase tracking-wide">
                        {category}
                    </h5>
                    {checklist.filter(item => item.category === category).map((item, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className={`checklist-item flex items-center gap-3 p-2 rounded hover:bg-white/5 cursor-pointer ${item.completed ? 'completed' : ''}`}
                            onClick={() => onToggle(item)}
                        >
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${item.completed ? 'bg-success border-success' : 'border-gray-500'}`}>
                                {item.completed && <Check className="w-3 h-3 text-white" />}
                            </div>
                            <span className="text-sm text-gray-300">{item.task}</span>
                        </motion.div>
                    ))}
                </div>
            ))}
        </div>
    );
};

export const BlueprintDisplay = ({ 
    blueprint, 
    idea, 
    onChecklistToggle, 
    onShare, 
    isSharing, 
    isAuthenticated, 
    isSharedView = false,
    isPro = false,
    usedClarification = false,
    clarificationSkipped = false,
    promptId = null,
    onUpgrade,
    projectId = null,
    onContinueBuildGenerated
}) => {
    const [openSections, setOpenSections] = useState({
        features: true,
        screens: false,
        database: false,
        api: false,
        build: false,
        checklist: false
    });
    const [copied, setCopied] = useState(false);
    const [showPromptModal, setShowPromptModal] = useState(false);
    const [selectedBuilder, setSelectedBuilder] = useState('emergent');
    const [promptCopied, setPromptCopied] = useState(false);
    
    // New build option states
    const [showBuildInstructions, setShowBuildInstructions] = useState(false);
    const [selectedBuildOption, setSelectedBuildOption] = useState(null);
    const [buildPromptCopied, setBuildPromptCopied] = useState(false);
    
    // Collaboration modal state
    const [showCollaborationModal, setShowCollaborationModal] = useState(false);

    const builderConfigs = getBuilderConfigs();

    const toggleSection = (section) => {
        setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    const handleCopy = async () => {
        const markdown = blueprintToMarkdown(blueprint, idea);
        const success = await copyToClipboard(markdown);
        if (success) {
            setCopied(true);
            toast.success('Architecture copied to clipboard!');
            trackBlueprintCopied();
            setTimeout(() => setCopied(false), 2000);
        } else {
            toast.error('Failed to copy. Please try again.');
        }
    };

    const handleDownload = () => {
        const markdown = blueprintToMarkdown(blueprint, idea);
        const filename = `${blueprint.app_name.toLowerCase().replace(/\s+/g, '-')}-architecture.md`;
        downloadMarkdown(markdown, filename);
        toast.success('Architecture downloaded!');
        trackBlueprintDownloaded(blueprint.app_name);
    };

    const handleCopyPrompt = async (builder = selectedBuilder) => {
        const prompt = generateBuilderPrompt(blueprint, idea, builder);
        const success = await copyToClipboard(prompt);
        if (success) {
            setPromptCopied(true);
            const builderName = builderConfigs[builder]?.name || 'AI Builder';
            toast.success(`${builderName} prompt copied! Paste it into ${builderName}.`);
            trackBuilderPromptCopied(builder);
            setTimeout(() => setPromptCopied(false), 2000);
        } else {
            toast.error('Failed to copy. Please try again.');
        }
    };

    const handleCopyQuickPrompt = async () => {
        const prompt = generateQuickPrompt(blueprint, idea);
        const success = await copyToClipboard(prompt);
        if (success) {
            setPromptCopied(true);
            toast.success('Quick prompt copied!');
            trackBuilderPromptCopied('quick');
            setTimeout(() => setPromptCopied(false), 2000);
        } else {
            toast.error('Failed to copy. Please try again.');
        }
    };

    const handleBuilderSelect = (builder) => {
        setSelectedBuilder(builder);
        trackBuilderSelected(builder);
    };

    // New build option handlers
    const handleBuildOptionClick = (option) => {
        setSelectedBuildOption(option);
        setShowBuildInstructions(true);
        setBuildPromptCopied(false);
        trackBuildOptionSelected(option, projectId);
    };

    const handleCopyBuildPrompt = async () => {
        const prompt = selectedBuildOption === 'chatgpt' 
            ? generateBuilderPrompt(blueprint, idea, 'chatgpt')
            : generateBuilderPrompt(blueprint, idea, 'cursor');
        
        const success = await copyToClipboard(prompt);
        if (success) {
            setBuildPromptCopied(true);
            toast.success('Prompt copied! Now paste it into your chosen tool.');
            setTimeout(() => setBuildPromptCopied(false), 3000);
        }
    };

    const [isLaunchingEmrg, setIsLaunchingEmrg] = useState(false);

    const handleLaunchEmrg = async () => {
        setIsLaunchingEmrg(true);
        
        // Track EMRG button click with fromSharedView flag
        const fromShared = isFromSharedView();
        trackEmrgButtonClicked(blueprint?.app_name, fromShared);
        
        // Clear the flag after tracking
        if (fromShared) {
            clearSharedViewFlag();
        }
        
        try {
            const result = await launchEmrg(blueprint, idea);
            if (result.success) {
                if (result.copySuccess) {
                    toast.success(
                        <div>
                            <strong>Your EMRG builder prompt has been copied.</strong>
                            <p className="text-sm mt-1">EMRG is opening in a new tab. Paste the prompt there to start building your app.</p>
                        </div>,
                        { duration: 6000 }
                    );
                } else {
                    toast.info(
                        <div>
                            <strong>EMRG is opening in a new tab.</strong>
                            <p className="text-sm mt-1">Click the "Copy" button on your blueprint first, then paste it in EMRG to start building.</p>
                        </div>,
                        { duration: 6000 }
                    );
                }
            } else {
                toast.error(result.error || 'Failed to launch EMRG');
            }
        } catch (error) {
            console.error('Error launching EMRG:', error);
            toast.error('Failed to launch EMRG. Please try again.');
        } finally {
            setIsLaunchingEmrg(false);
        }
    };

    if (!blueprint) return null;

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
            data-testid="blueprint-display"
        >
            {/* Header */}
            <div className="glass-card p-6">
                <div className="mb-3">
                    <span className="text-xs uppercase tracking-wide text-primary font-medium">
                        {isSharedView ? 'Shared App Architecture' : 'Generated App Architecture'}
                    </span>
                </div>
                <div className="flex flex-col gap-4">
                    <div>
                        <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-2">
                            {blueprint.app_name}
                        </h2>
                        <p className="text-gray-400">{blueprint.summary}</p>
                    </div>
                    
                    {/* Action Bar - Clear and Explicit */}
                    <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-white/5">
                        <TooltipProvider delayDuration={300}>
                            {/* Copy Blueprint */}
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        data-testid="copy-blueprint-btn"
                                        variant="outline"
                                        size="sm"
                                        onClick={handleCopy}
                                        className="bg-white/5 border-white/10 hover:bg-white/10"
                                    >
                                        {copied ? <Check className="w-4 h-4 mr-2 text-green-400" /> : <FileText className="w-4 h-4 mr-2" />}
                                        {copied ? 'Copied!' : 'Copy Blueprint'}
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="bg-zinc-900 border-zinc-700">
                                    <p className="text-xs">Copy the full blueprint as markdown</p>
                                </TooltipContent>
                            </Tooltip>

                            {/* Download */}
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        data-testid="download-blueprint-btn"
                                        variant="outline"
                                        size="sm"
                                        onClick={handleDownload}
                                        className="bg-white/5 border-white/10 hover:bg-white/10"
                                    >
                                        <Download className="w-4 h-4 mr-2" />
                                        Download
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="bg-zinc-900 border-zinc-700">
                                    <p className="text-xs">Download as .md file</p>
                                </TooltipContent>
                            </Tooltip>

                            {/* Share Link - Only show when not in shared view */}
                            {!isSharedView && onShare && (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            data-testid="share-blueprint-btn"
                                            variant="outline"
                                            size="sm"
                                            onClick={onShare}
                                            disabled={isSharing || !isAuthenticated}
                                            className={`bg-white/5 border-white/10 hover:bg-white/10 ${
                                                !isAuthenticated ? 'opacity-50' : ''
                                            }`}
                                        >
                                            {isSharing ? (
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            ) : (
                                                <Link className="w-4 h-4 mr-2" />
                                            )}
                                            {isSharing ? 'Creating...' : 'Get Share Link'}
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="bottom" className="bg-zinc-900 border-zinc-700">
                                        <p className="text-xs">
                                            {!isAuthenticated 
                                                ? 'Sign in to create a shareable link' 
                                                : 'Create a public link and copy to clipboard'
                                            }
                                        </p>
                                    </TooltipContent>
                                </Tooltip>
                            )}
                        </TooltipProvider>
                    </div>
                </div>
            </div>

            {/* Continue Building (Pro Feature) - Primary action after seeing the blueprint */}
            {!isSharedView && (
                <ContinueBuilding
                    blueprintId={blueprint.id || `bp-${Date.now()}`}
                    originalIdea={idea}
                    blueprintSummary={blueprint.summary}
                    projectId={projectId}
                    isPro={isPro}
                    onUpgrade={onUpgrade}
                    onContentGenerated={onContinueBuildGenerated}
                />
            )}

            {/* Build This App in EMRG - Primary CTA */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="relative overflow-hidden rounded-xl"
                data-testid="emrg-cta-section"
            >
                <div className="absolute inset-0 bg-gradient-to-r from-violet-600/20 via-fuchsia-600/20 to-violet-600/20"></div>
                <div className="relative p-6 border-2 border-violet-500/40" style={{ boxShadow: '0 0 30px rgba(139, 92, 246, 0.25)' }}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-start gap-4">
                            <div className="p-3 rounded-xl bg-gradient-to-br from-violet-500/40 to-fuchsia-500/40">
                                <Rocket className="w-6 h-6 text-violet-300" />
                            </div>
                            <div>
                                <h3 className="font-heading font-semibold text-lg text-white mb-1">
                                    Ready to Build?
                                </h3>
                                <p className="text-sm text-zinc-400">
                                    Turn this blueprint into a working app with EMRG's AI builder.
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                            <TooltipProvider delayDuration={300}>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            data-testid="build-in-emrg-btn"
                                            onClick={handleLaunchEmrg}
                                            disabled={isLaunchingEmrg}
                                            className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white rounded-xl px-6 py-3 font-semibold transition-all hover:scale-[1.02] hover:shadow-[0_0_20px_rgba(139,92,246,0.4)]"
                                        >
                                            {isLaunchingEmrg ? (
                                                <>
                                                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                                    Opening EMRG...
                                                </>
                                            ) : (
                                                <>
                                                    <Rocket className="w-5 h-5 mr-2" />
                                                    Build in EMRG
                                                </>
                                            )}
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="bottom" className="bg-zinc-900 border-zinc-700 max-w-xs">
                                        <p className="text-xs">Copies a builder prompt to your clipboard and opens EMRG in a new tab</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                            <p className="text-xs text-zinc-500 text-right">
                                Copies prompt & opens EMRG in new tab
                            </p>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Build This App Section - Redesigned for clarity */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="glass-card overflow-hidden"
                data-testid="ai-prompt-section"
            >
                {/* Header */}
                <div className="p-6 border-b border-white/10">
                    <div className="flex items-start gap-4">
                        <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20">
                            <Rocket className="w-6 h-6 text-blue-400" />
                        </div>
                        <div>
                            <h3 className="font-heading font-semibold text-xl text-white mb-1">
                                🚀 Build This App
                            </h3>
                            <p className="text-sm text-gray-400">
                                Choose how you want to bring this to life:
                            </p>
                        </div>
                    </div>
                </div>

                {/* Build Options */}
                <div className="p-6 grid md:grid-cols-3 gap-4">
                    {/* Emergent - Visual Builder */}
                    <button
                        onClick={() => { trackBuildOptionSelected('emergent', projectId); handleLaunchEmrg(); }}
                        disabled={isLaunchingEmrg}
                        className="group relative p-5 rounded-xl border-2 border-violet-500/30 bg-gradient-to-br from-violet-500/10 to-fuchsia-500/10 hover:border-violet-500/60 hover:from-violet-500/20 hover:to-fuchsia-500/20 transition-all text-left"
                        data-testid="build-option-emergent"
                    >
                        <div className="absolute top-3 right-3">
                            <span className="px-2 py-0.5 text-[10px] font-medium bg-green-500/20 text-green-400 rounded-full border border-green-500/30">
                                Beginner Friendly
                            </span>
                        </div>
                        <div className="p-2.5 rounded-lg bg-violet-500/20 w-fit mb-3">
                            <Layout className="w-5 h-5 text-violet-400" />
                        </div>
                        <h4 className="font-semibold text-white mb-1.5 group-hover:text-violet-300 transition-colors">
                            Build visually (no coding)
                        </h4>
                        <p className="text-xs text-gray-400 leading-relaxed mb-1">
                            Turn this blueprint into a working app using a drag-and-drop builder
                        </p>
                        <p className="text-[11px] text-violet-400/80 font-medium">
                            No coding needed — just follow the steps
                        </p>
                        <div className="mt-3 pt-3 border-t border-white/5">
                            <p className="text-[10px] text-gray-600 flex items-center gap-1">
                                <Sparkles className="w-3 h-3" />
                                Opens Emergent AI in new tab
                            </p>
                        </div>
                    </button>

                    {/* ChatGPT - Guided */}
                    <button
                        onClick={() => handleBuildOptionClick('chatgpt')}
                        className="group relative p-5 rounded-xl border border-zinc-700 bg-zinc-800/50 hover:border-emerald-500/40 hover:bg-zinc-800 transition-all text-left"
                        data-testid="build-option-chatgpt"
                    >
                        <div className="absolute top-3 right-3">
                            <span className="px-2 py-0.5 text-[10px] font-medium bg-emerald-500/20 text-emerald-400 rounded-full border border-emerald-500/30">
                                Guided
                            </span>
                        </div>
                        <div className="p-2.5 rounded-lg bg-emerald-500/20 w-fit mb-3">
                            <MessageSquare className="w-5 h-5 text-emerald-400" />
                        </div>
                        <h4 className="font-semibold text-white mb-1.5 group-hover:text-emerald-300 transition-colors">
                            Step-by-step guidance
                        </h4>
                        <p className="text-xs text-gray-400 leading-relaxed mb-1">
                            Build this app with AI walking you through each step
                        </p>
                        <p className="text-[11px] text-emerald-400/80 font-medium">
                            Great for learning while building
                        </p>
                        <div className="mt-3 pt-3 border-t border-white/5">
                            <p className="text-[10px] text-gray-600 flex items-center gap-1">
                                <Copy className="w-3 h-3" />
                                Copies prompt to paste into ChatGPT
                            </p>
                        </div>
                    </button>

                    {/* Cursor/Replit - Developer */}
                    <button
                        onClick={() => handleBuildOptionClick('cursor')}
                        className="group relative p-5 rounded-xl border border-zinc-700 bg-zinc-800/50 hover:border-blue-500/40 hover:bg-zinc-800 transition-all text-left"
                        data-testid="build-option-developer"
                    >
                        <div className="absolute top-3 right-3">
                            <span className="px-2 py-0.5 text-[10px] font-medium bg-blue-500/20 text-blue-400 rounded-full border border-blue-500/30">
                                Developer
                            </span>
                        </div>
                        <div className="p-2.5 rounded-lg bg-blue-500/20 w-fit mb-3">
                            <Code className="w-5 h-5 text-blue-400" />
                        </div>
                        <h4 className="font-semibold text-white mb-1.5 group-hover:text-blue-300 transition-colors">
                            Build with code
                        </h4>
                        <p className="text-xs text-gray-400 leading-relaxed mb-1">
                            Generate code using Cursor, Replit, or similar tools
                        </p>
                        <p className="text-[11px] text-blue-400/80 font-medium">
                            For developers who prefer code
                        </p>
                        <div className="mt-3 pt-3 border-t border-white/5">
                            <p className="text-[10px] text-gray-600 flex items-center gap-1">
                                <Terminal className="w-3 h-3" />
                                Optimized for AI coding assistants
                            </p>
                        </div>
                    </button>
                </div>
            </motion.div>

            {/* Collaborate Section */}
            {!isSharedView && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                    className="glass-card p-5"
                    data-testid="collaboration-section"
                >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-start gap-3">
                            <div className="p-2.5 rounded-lg bg-gradient-to-br from-violet-500/20 to-pink-500/20">
                                <Users className="w-5 h-5 text-violet-400" />
                            </div>
                            <div>
                                <h4 className="font-medium text-white mb-0.5">
                                    Need help building this?
                                </h4>
                                <p className="text-sm text-gray-500">
                                    Find a developer, designer, or co-founder to help bring this to life
                                </p>
                            </div>
                        </div>
                        <Button
                            onClick={() => setShowCollaborationModal(true)}
                            variant="outline"
                            className="bg-gradient-to-r from-violet-500/10 to-pink-500/10 border-violet-500/30 hover:border-violet-500/50 hover:from-violet-500/20 hover:to-pink-500/20 rounded-xl px-5"
                            data-testid="collaborate-btn"
                        >
                            <Users className="w-4 h-4 mr-2" />
                            🤝 Collaborate on this idea
                        </Button>
                    </div>
                </motion.div>
            )}

            {/* Collaboration Modal */}
            <CollaborationModal
                isOpen={showCollaborationModal}
                onClose={() => setShowCollaborationModal(false)}
                projectId={projectId}
                ideaSummary={blueprint?.summary || idea}
            />

            {/* Build Option Modal - Shows after selection */}
            <AnimatePresence>
                {showBuildInstructions && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={() => setShowBuildInstructions(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="glass-card w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
                            onClick={(e) => e.stopPropagation()}
                            data-testid="build-instructions-modal"
                        >
                            {/* Modal Header */}
                            <div className="flex items-center justify-between p-5 border-b border-white/10">
                                <div className="flex items-center gap-3">
                                    <div className={`p-2 rounded-lg ${
                                        selectedBuildOption === 'chatgpt' ? 'bg-emerald-500/20' : 'bg-blue-500/20'
                                    }`}>
                                        {selectedBuildOption === 'chatgpt' 
                                            ? <MessageSquare className="w-5 h-5 text-emerald-400" />
                                            : <Code className="w-5 h-5 text-blue-400" />
                                        }
                                    </div>
                                    <div>
                                        <h3 className="font-heading font-semibold text-lg">
                                            {selectedBuildOption === 'chatgpt' ? 'Build with ChatGPT' : 'Build with Code'}
                                        </h3>
                                        <p className="text-xs text-gray-500">Follow these steps to build your app</p>
                                    </div>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setShowBuildInstructions(false)}
                                    className="hover:bg-white/10"
                                >
                                    <X className="w-5 h-5" />
                                </Button>
                            </div>

                            {/* Instructions */}
                            <div className="p-5 border-b border-white/10">
                                <h4 className="font-medium text-white mb-3 flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-green-400" />
                                    How to use this prompt:
                                </h4>
                                {selectedBuildOption === 'chatgpt' ? (
                                    <ol className="space-y-2 text-sm text-gray-400">
                                        <li className="flex gap-2">
                                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 text-xs flex items-center justify-center">1</span>
                                            <span>Copy the prompt below</span>
                                        </li>
                                        <li className="flex gap-2">
                                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 text-xs flex items-center justify-center">2</span>
                                            <span>Open <a href="https://chat.openai.com" target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:underline">chat.openai.com</a> or your preferred AI chat</span>
                                        </li>
                                        <li className="flex gap-2">
                                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 text-xs flex items-center justify-center">3</span>
                                            <span>Paste the prompt and start a conversation</span>
                                        </li>
                                        <li className="flex gap-2">
                                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 text-xs flex items-center justify-center">4</span>
                                            <span>The AI will guide you through building each part step by step</span>
                                        </li>
                                    </ol>
                                ) : (
                                    <ol className="space-y-2 text-sm text-gray-400">
                                        <li className="flex gap-2">
                                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-500/20 text-blue-400 text-xs flex items-center justify-center">1</span>
                                            <span>Copy the prompt below</span>
                                        </li>
                                        <li className="flex gap-2">
                                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-500/20 text-blue-400 text-xs flex items-center justify-center">2</span>
                                            <span>Open your coding tool (<a href="https://cursor.sh" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Cursor</a>, <a href="https://replit.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Replit</a>, or <a href="https://bolt.new" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Bolt</a>)</span>
                                        </li>
                                        <li className="flex gap-2">
                                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-500/20 text-blue-400 text-xs flex items-center justify-center">3</span>
                                            <span>Paste into the AI chat or command palette</span>
                                        </li>
                                        <li className="flex gap-2">
                                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-500/20 text-blue-400 text-xs flex items-center justify-center">4</span>
                                            <span>The AI will generate your project files and code</span>
                                        </li>
                                    </ol>
                                )}
                            </div>

                            {/* Prompt Content */}
                            <div className="flex-1 overflow-auto p-5">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Your Build Prompt</p>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleCopyBuildPrompt()}
                                        className="text-xs"
                                    >
                                        {buildPromptCopied ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                                        {buildPromptCopied ? 'Copied!' : 'Copy'}
                                    </Button>
                                </div>
                                <pre className="text-sm text-gray-300 whitespace-pre-wrap font-mono bg-black/30 p-4 rounded-lg border border-white/5 leading-relaxed max-h-[200px] overflow-auto">
                                    {selectedBuildOption === 'chatgpt' 
                                        ? generateBuilderPrompt(blueprint, idea, 'chatgpt')
                                        : generateBuilderPrompt(blueprint, idea, 'cursor')
                                    }
                                </pre>
                            </div>

                            {/* Modal Footer */}
                            <div className="p-5 border-t border-white/10">
                                <Button
                                    onClick={() => handleCopyBuildPrompt()}
                                    className={`w-full ${
                                        selectedBuildOption === 'chatgpt' 
                                            ? 'bg-emerald-600 hover:bg-emerald-500' 
                                            : 'bg-blue-600 hover:bg-blue-500'
                                    } text-white rounded-xl py-3`}
                                >
                                    {buildPromptCopied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                                    {buildPromptCopied ? 'Copied to Clipboard!' : 'Copy Prompt & Start Building'}
                                </Button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Features Section */}
            <div className="glass-card overflow-hidden">
                <SectionHeader
                    icon={Layers}
                    title="Features"
                    count={blueprint.features.length}
                    isOpen={openSections.features}
                    onClick={() => toggleSection('features')}
                    color="bg-primary/20 text-primary"
                />
                <AnimatePresence>
                    {openSections.features && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                        >
                            <div className="p-4 pt-0 grid gap-3">
                                {blueprint.features.map((feature, i) => (
                                    <FeatureCard key={i} feature={feature} index={i} />
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Screens Section */}
            <div className="glass-card overflow-hidden">
                <SectionHeader
                    icon={Layout}
                    title="Screens / Pages"
                    count={blueprint.screens.length}
                    isOpen={openSections.screens}
                    onClick={() => toggleSection('screens')}
                    color="bg-accent/20 text-accent"
                />
                <AnimatePresence>
                    {openSections.screens && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                        >
                            <div className="p-4 pt-0 grid md:grid-cols-2 gap-3">
                                {blueprint.screens.map((screen, i) => (
                                    <ScreenCard key={i} screen={screen} index={i} />
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Database Section */}
            <div className="glass-card overflow-hidden">
                <SectionHeader
                    icon={Database}
                    title="Database Structure"
                    count={blueprint.database.length}
                    isOpen={openSections.database}
                    onClick={() => toggleSection('database')}
                    color="bg-success/20 text-success"
                />
                <AnimatePresence>
                    {openSections.database && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                        >
                            <div className="p-4 pt-0 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                                {blueprint.database.map((table, i) => (
                                    <DatabaseCard key={i} table={table} index={i} />
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* API Endpoints Section */}
            <div className="glass-card overflow-hidden">
                <SectionHeader
                    icon={Globe}
                    title="API Endpoints"
                    count={blueprint.api_endpoints.length}
                    isOpen={openSections.api}
                    onClick={() => toggleSection('api')}
                    color="bg-secondary/20 text-secondary"
                />
                <AnimatePresence>
                    {openSections.api && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                        >
                            <div className="p-4 pt-0 space-y-2">
                                {blueprint.api_endpoints.map((endpoint, i) => (
                                    <APIEndpointRow key={i} endpoint={endpoint} index={i} />
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Build Order Section */}
            <div className="glass-card overflow-hidden">
                <SectionHeader
                    icon={ListOrdered}
                    title="Build Order"
                    count={blueprint.build_order.length}
                    isOpen={openSections.build}
                    onClick={() => toggleSection('build')}
                    color="bg-warning/20 text-warning"
                />
                <AnimatePresence>
                    {openSections.build && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                        >
                            <div className="p-4 pt-0">
                                {blueprint.build_order.map((step, i) => (
                                    <BuildPhase key={i} step={step} index={i} />
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Checklist Section */}
            <div className="glass-card overflow-hidden">
                <SectionHeader
                    icon={CheckSquare}
                    title="Development Checklist"
                    count={blueprint.checklist.length}
                    isOpen={openSections.checklist}
                    onClick={() => toggleSection('checklist')}
                    color="bg-primary/20 text-primary"
                />
                <AnimatePresence>
                    {openSections.checklist && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                        >
                            <div className="p-4 pt-0">
                                <ChecklistSection 
                                    checklist={blueprint.checklist} 
                                    onToggle={onChecklistToggle}
                                />
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Blueprint Rating */}
            {!isSharedView && (
                <div className="glass-card p-4">
                    <BlueprintRating
                        blueprintId={blueprint.id || `bp-${Date.now()}`}
                        usedClarification={usedClarification}
                        clarificationSkipped={clarificationSkipped}
                        promptId={promptId}
                        onImprove={() => {
                            // Scroll to top to regenerate
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                            toast.info('Try regenerating with a more detailed prompt!');
                        }}
                    />
                </div>
            )}
        </motion.div>
    );
};
